// The Sell screen's server state (spec/13 §4, F-POS-09/-01/-12), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/commerce.ts` and `.../food.ts`:
//
//   GET  …/venues/{v}/menu                    the venue's effective menu           products:read
//   POST …/venues/{v}/sales                   create a sale of mixed lines         orders:create
//   POST …/venues/{v}/sales/quote             price the cart, promotions and all   orders:create
//   POST …/venues/{v}/sales/{id}/payments     take a tender (split tender)         orders:create
//   POST …/venues/{v}/sales/{id}/on-account   charge it to the player's account    orders:on_account
//   GET  …/players/{p}/wallet/balance         the attached player's wallet         players:read
//   POST …/venues/{v}/sales/{id}/discount-code apply a discount code (F-POS-02)    orders:create
//   POST …/venues/{v}/coupons/redeem          redeem a coupon (F-POS-06)           orders:create
//
// The server prices every line and computes the GST; the client only chooses what to sell and how it
// is paid (spec/13 §4). A tender of `wallet` draws the attached player's wallet (F-WAL-04); on account
// charges their receivable (F-POS-07). Both need a player on the sale.

import type { CounterUpi, CreateSaleRequest, QuoteSaleRequest, SaleQuote, TakeSaleOnAccountRequest, TakeSalePaymentRequest, VenueMenu } from '@clavian/api-client';
import { upiPayUri } from '@clavian/domain/upi';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useApi } from '../../api/client';
import { walletKeys } from '../wallet/queries';

export const sellKeys = {
  menu: (organizationId: string, venueId: string) => ['sell', organizationId, venueId, 'menu'] as const,
  quote: (organizationId: string, venueId: string, cart: string | null) => ['sell', organizationId, venueId, 'quote', cart] as const,
};

/** The venue's effective menu — its products with the venue's price and availability applied. */
export function useVenueMenu(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<VenueMenu>({
    queryKey: sellKeys.menu(organizationId, venueId),
    queryFn: () => api.getVenueMenu({ path: { organizationId, venueId } }),
  });
}

/** How long the cart rests before it is quoted again: a burst of taps is one quote, not one per tap. */
const QUOTE_DEBOUNCE_MS = 300;

/**
 * The cart priced by the server before it is placed (F-POS-03, `quoteSale`): its automatic promotions and
 * the total they leave — the same pricing path the sale takes, with nothing written. `cart` is the body
 * the sale would be placed with, or `null` for nothing to quote. It is quoted once the cart has rested
 * for a moment, and the last quote stays in `data` while the next one loads, so the summary never
 * flickers. A refused quote is not retried: the cart falls back to its own sum, and placing the sale
 * stays the authority.
 */
export function useSaleQuote(organizationId: string, venueId: string, cart: QuoteSaleRequest | null) {
  const api = useApi();
  const wanted = cart === null ? null : JSON.stringify(cart);
  const [settled, setSettled] = useState(wanted);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(wanted), QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [wanted]);
  return useQuery<SaleQuote>({
    queryKey: sellKeys.quote(organizationId, venueId, settled),
    queryFn: () => api.quoteSale({ path: { organizationId, venueId }, body: JSON.parse(settled ?? '{}') as QuoteSaleRequest }),
    enabled: settled !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/** Create a sale (F-POS-01): the server prices the basket and places it. */
export function useCreateSale(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    // A fresh `Idempotency-Key` per attempt: a request the network retries places the sale once (spec/08 §2.2).
    mutationFn: (body: CreateSaleRequest) => api.createSale({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
  });
}

/**
 * Take a tender against a sale (F-POS-12): split across several is allowed. A wallet tender moves the
 * player's balance, so the cached balance is re-read after it.
 */
export function useTakeSalePayment(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ saleId, body }: { saleId: string; body: TakeSalePaymentRequest }) =>
      api.takeSalePayment({ path: { organizationId, venueId, saleId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: (sale, { body }) => {
      // A wallet tender draws the player's wallet; change sent to a wallet credits one. Either way its balance and its
      // statement are read again, so the player's Wallet tab shows the movement.
      if (body.tender === 'wallet' && sale.playerId !== null) {
        void queryClient.invalidateQueries({ queryKey: walletKeys.player(organizationId, sale.playerId) });
      }
      const changeTo = body.changeToWallet === true ? (body.changeToWalletPlayerId ?? sale.playerId) : null;
      if (changeTo !== null && changeTo !== undefined) void queryClient.invalidateQueries({ queryKey: walletKeys.player(organizationId, changeTo) });
    },
  });
}

/** Charge a sale (the outstanding, or part of it) to its player's account (F-POS-07, `orders:on_account`). */
export function useTakeSaleOnAccount(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ saleId, body }: { saleId: string; body: TakeSaleOnAccountRequest }) =>
      api.takeSaleOnAccount({ path: { organizationId, venueId, saleId }, body }),
  });
}

/** Apply a discount code to a placed, unpaid sale (F-POS-02, `orders:create`): the server re-prices it. */
export function useApplySaleCode(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ saleId, code }: { saleId: string; code: string }) => api.applyDiscountCode({ path: { organizationId, venueId, saleId }, body: { code } }),
  });
}

/**
 * Redeem a coupon for the sale's player (F-POS-06, `orders:create`): it grants a wallet bonus, so the
 * cached balance is re-read after it — the Wallet tender can then spend it.
 */
export function useRedeemCoupon(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ playerId, code }: { playerId: string; code: string }) => api.redeemCoupon({ path: { organizationId, venueId }, body: { code, playerId } }),
    onSuccess: (_redemption, { playerId }) => queryClient.invalidateQueries({ queryKey: walletKeys.player(organizationId, playerId) }),
  });
}

/**
 * The venue's own UPI ID for the counter UPI QR (F-PAY-02, `orders:read`): Sell draws it for the amount due when UPI
 * is chosen. `vpa` is null when the venue has not set one; counter UPI is then recorded with no QR, as before.
 */
export function useCounterUpi(organizationId: string, venueId: string) {
  const api = useApi();
  return useQuery<CounterUpi>({
    queryKey: ['sell', organizationId, venueId, 'counter-upi'],
    queryFn: () => api.getCounterUpi({ path: { organizationId, venueId } }),
    staleTime: 60_000,
  });
}

/**
 * Bill a placed, unpaid sale to a business (F-CMP-07, `orders:create`): its GSTIN and name. A GSTIN from another state
 * than the venue's makes the server split the GST as IGST — the same tax, so the total does not change.
 */
export function useSetSaleBuyer(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: ({ saleId, gstin, name }: { saleId: string; gstin: string; name: string }) =>
      api.setSaleBuyer({ path: { organizationId, venueId, saleId }, body: { gstin, name } }),
  });
}

/**
 * A `upi://pay` intent for the venue's own QR: payee, name, amount in rupees and the note (NPCI's link spec) — the one
 * builder the kiosk's "Scan & pay (UPI)" draws too (`upiPayUri`, `@clavian/domain`).
 */
export function counterUpiIntent(vpa: string, payeeName: string, amountMinor: number, note: string): string {
  return upiPayUri({ vpa, payeeName, amount: { amount: amountMinor, currency: 'INR' }, note });
}
